import type { PropsWithChildren, JSX } from 'react';
import styled from 'styled-components';
import { useAppSelector, type RootState } from '~/store/types';

export const sizes = {
	small: 767,
	medium: 992,
	large: 1200,
	extraLarge: 1750
} as const;

interface BrowserState {
	is: {
		small: boolean;
		medium: boolean;
		large: boolean;
		extraLarge: boolean;
	};
	lessThan: {
		small: boolean;
		medium: boolean;
		large: boolean;
		extraLarge: boolean;
	};
	greaterThan: {
		small: boolean;
		medium: boolean;
		large: boolean;
		extraLarge: boolean;
	};
}

interface State {
	browser: BrowserState;
}

interface ConditionalProps {
	condition?: boolean;
	render?: () => JSX.Element | null;
}

/**
 * @deprecated This code is deprecated and should not be used in new implementations.
 */
const Conditional = ({ condition, children, render = undefined }: PropsWithChildren<ConditionalProps>) =>
	(condition && ((children && <>{children}</>) || (render && render()))) || // eslint-disable-line react/jsx-no-useless-fragment
	null;

interface MediaQueryProps extends PropsWithChildren {
	render?: () => JSX.Element | null;
}

/**
 * @deprecated This code is deprecated and should not be used in new implementations.
 */
const smallSelector = ({ browser: { is } }: State) => ({
	condition: is.small
});

/**
 * @deprecated This code is deprecated and should not be used in new implementations.
 */
const isSmallSelector = (state: RootState) => smallSelector(state as unknown as State).condition;

/**
 * @deprecated This code is deprecated and should not be used in new implementations.
 */
const Small = ({ children, render = undefined }: MediaQueryProps) => {
	const isSmall = useAppSelector(isSmallSelector);
	return (
		<Conditional condition={isSmall} render={render}>
			{children}
		</Conditional>
	);
};

/**
 * @deprecated This code is deprecated and should not be used in new implementations.
 */
const mediumSelector = ({ browser: { is } }: State) => ({
	condition: is.medium
});

/**
 * @deprecated This code is deprecated and should not be used in new implementations.
 */
const Medium = ({ children, render = undefined }: MediaQueryProps) => {
	const isMedium = useAppSelector((state: RootState) => mediumSelector(state as unknown as State).condition);
	return (
		<Conditional condition={isMedium} render={render}>
			{children}
		</Conditional>
	);
};

/**
 * @deprecated This code is deprecated and should not be used in new implementations.
 */
const lessThanLargeSelector = ({ browser: { lessThan } }: State) => ({
	condition: lessThan.large
});

/**
 * @deprecated This code is deprecated and should not be used in new implementations.
 */
const LessThanLarge = ({ children, render = undefined }: MediaQueryProps) => {
	const isLessThanLarge = useAppSelector(
		(state: RootState) => lessThanLargeSelector(state as unknown as State).condition
	);
	return (
		<Conditional condition={isLessThanLarge} render={render}>
			{children}
		</Conditional>
	);
};

/**
 * @deprecated This code is deprecated and should not be used in new implementations.
 */
const biggerSelector = ({ browser: { greaterThan } }: State) => ({
	condition: greaterThan.small
});

/**
 * @deprecated This code is deprecated and should not be used in new implementations.
 */
const isBiggerSelector = (state: RootState) => biggerSelector(state as unknown as State).condition;

/**
 * @deprecated This code is deprecated and should not be used in new implementations.
 */
const Bigger = ({ children, render = undefined }: MediaQueryProps) => {
	const isBigger = useAppSelector(isBiggerSelector);
	return (
		<Conditional condition={isBigger} render={render}>
			{children}
		</Conditional>
	);
};

/**
 * @deprecated This code is deprecated and should not be used in new implementations.
 */
const biggerThanMediumSelector = ({ browser: { greaterThan } }: State) => ({
	condition: greaterThan.medium
});

/**
 * @deprecated This code is deprecated and should not be used in new implementations.
 */
const isBiggerThanMediumSelector = (state: RootState) => biggerThanMediumSelector(state as unknown as State).condition;

/**
 * @deprecated This code is deprecated and should not be used in new implementations.
 */
const BiggerThanMedium = ({ children, render = undefined }: MediaQueryProps) => {
	const isBiggerThanMedium = useAppSelector(isBiggerThanMediumSelector);
	return (
		<Conditional condition={isBiggerThanMedium} render={render}>
			{children}
		</Conditional>
	);
};

/**
 * @deprecated This code is deprecated and should not be used in new implementations.
 */
const biggerThanLargeSelector = ({ browser: { greaterThan } }: State) => ({
	condition: greaterThan.large
});

/**
 * @deprecated This code is deprecated and should not be used in new implementations.
 */
const isBiggerThanLargeSelector = (state: RootState) => biggerThanLargeSelector(state as unknown as State).condition;

/**
 * @deprecated This code is deprecated and should not be used in new implementations.
 */
const BiggerThanLarge = ({ children, render = undefined }: MediaQueryProps) => {
	const isBiggerThanLarge = useAppSelector(isBiggerThanLargeSelector);
	return (
		<Conditional condition={isBiggerThanLarge} render={render}>
			{children}
		</Conditional>
	);
};

/**
 * @deprecated This code is deprecated and should not be used in new implementations.
 * Could be dangerous to set this to is small in case we would
 * create an extra small bp in the future.
 */
const smallerSelector = ({ browser: { lessThan } }: State) => ({
	condition: lessThan.medium
});

/**
 * @deprecated This code is deprecated and should not be used in new implementations.
 */
const isSmallerSelector = (state: RootState) => smallerSelector(state as unknown as State).condition;

/**
 * @deprecated This code is deprecated and should not be used in new implementations.
 */
const Smaller = ({ children, render = undefined }: MediaQueryProps) => {
	const isSmaller = useAppSelector(isSmallerSelector);
	return (
		<Conditional condition={isSmaller} render={render}>
			{children}
		</Conditional>
	);
};

/**
 * @deprecated This code is deprecated and should not be used in new implementations.
 */
const biggerThanExtraLargeSelector = ({ browser: { greaterThan } }: State) => ({
	condition: greaterThan.extraLarge
});

/**
 * @deprecated This code is deprecated and should not be used in new implementations.
 */
const isBiggerThanExtraLargeSelector = (state: State) => biggerThanExtraLargeSelector(state).condition;

/**
 * @deprecated This code is deprecated and should not be used in new implementations.
 */
const BiggerThanExtraLarge = ({ children, render = undefined }: MediaQueryProps) => {
	const isBiggerThanExtraLarge = useAppSelector(
		(state: RootState) => biggerThanExtraLargeSelector(state as unknown as State).condition
	);
	return (
		<Conditional condition={isBiggerThanExtraLarge} render={render}>
			{children}
		</Conditional>
	);
};

// 0px - 767px
const SmallContainer = styled.div`
	display: contents;
	/* New syntax (commented for future reference) REVC-240
	@media (width > ${sizes.small}px) {
		display: none;
	} */
	@media (min-width: ${sizes.small + 1}px) {
		display: none;
	}
`;

// 0px - 767px
const SmallerContainer = styled.div`
	display: contents;
	/* New syntax (commented for future reference) REVC-240
	@media (width > ${sizes.small}px) {
		display: none;
	} */
	@media (min-width: ${sizes.small + 1}px) {
		display: none;
	}
`;

// 767px - Infinity
const BiggerContainer = styled.div`
	display: contents;
	/* New syntax (commented for future reference) REVC-240
	@media (width <= ${sizes.small}px) {
		display: none;
	} */
	@media (max-width: ${sizes.small}px) {
		display: none;
	}
`;

// 768px - 992px
const MediumContainer = styled.div`
	display: contents;
	/* New syntax (commented for future reference) REVC-240
	@media (width <= ${sizes.small}px) or (width > ${sizes.medium}px) {
		display: none;
	} */
	@media (max-width: ${sizes.small}px), (min-width: ${sizes.medium + 1}px) {
		display: none;
	}
`;

// 0px - 992px
const LessThanLargeContainer = styled.div`
	display: contents;
	/* New syntax (commented for future reference) REVC-240
	@media (width > ${sizes.medium}px) {
		display: none;
	} */
	@media (min-width: ${sizes.medium + 1}px) {
		display: none;
	}
`;

// 992px - Infinity
const BiggerThanMediumContainer = styled.div`
	display: contents;
	/* New syntax (commented for future reference) REVC-240
	@media (width <= ${sizes.medium}px) {
		display: none;
	} */
	@media (max-width: ${sizes.medium}px) {
		display: none;
	}
`;

// 1200px - Infinity
const BiggerThanLargeContainer = styled.div`
	display: contents;
	/* New syntax (commented for future reference) REVC-240
	@media (width <= ${sizes.large}px) {
		display: none;
	} */
	@media (max-width: ${sizes.large}px) {
		display: none;
	}
`;

// 1750px - Infinity
const BiggerThanExtraLargeContainer = styled.div`
	display: contents;
	/* New syntax (commented for future reference) REVC-240
	@media (width <= ${sizes.extraLarge}px) {
		display: none;
	} */
	@media (max-width: ${sizes.extraLarge}px) {
		display: none;
	}
`;

export {
	Small,
	Medium,
	LessThanLarge,
	Bigger,
	BiggerThanMedium,
	BiggerThanLarge,
	Smaller,
	isSmallSelector,
	isSmallerSelector,
	isBiggerSelector,
	isBiggerThanLargeSelector,
	isBiggerThanMediumSelector,
	isBiggerThanExtraLargeSelector,
	BiggerThanExtraLarge,
	SmallContainer,
	SmallerContainer,
	MediumContainer,
	LessThanLargeContainer,
	BiggerContainer,
	BiggerThanMediumContainer,
	BiggerThanLargeContainer,
	BiggerThanExtraLargeContainer
};
